import React from 'react';
import Layout from '../components/Layout';
import { Accessibility, Eye, Keyboard, MousePointer, Volume2, Type } from 'lucide-react';

const Barrierefreiheit = ({ user }) => {
  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}}>
          <div className="p-6 md:p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <Accessibility className="w-8 h-8 text-blue-600" />
              Barrierefreiheit
            </h1>
            <p className="text-gray-600 mb-6">Unser Engagement für eine zugängliche Software</p>
            
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Unser Versprechen</h2>
                <p className="text-gray-700 leading-relaxed">Easyclaim24 verpflichtet sich, unsere Software für alle Benutzer zugänglich zu machen. Wir arbeiten kontinuierlich daran, die Zugänglichkeit und Benutzerfreundlichkeit unserer Software zu verbessern und dabei die Web Content Accessibility Guidelines (WCAG) 2.1 Level AA zu erfüllen.</p>
              </div>

              <div className="border-t pt-6" style={{borderColor: '#E1E4E8'}}>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Implementierte Funktionen</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-3">
                      <Keyboard className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Tastaturnavigation</h3>
                        <p className="text-sm text-gray-700">Vollständige Navigation durch die Software nur mit der Tastatur möglich. Alle Funktionen sind ohne Maus erreichbar.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-start gap-3">
                      <Eye className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Hoher Kontrast</h3>
                        <p className="text-sm text-gray-700">Optimierte Farbkontraste für bessere Lesbarkeit. Unterstützung für Dark Mode und Light Mode.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <div className="flex items-start gap-3">
                      <Type className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Skalierbare Schriften</h3>
                        <p className="text-sm text-gray-700">Alle Texte können über die Browser-Einstellungen vergrößert werden ohne dass die Funktionalität verloren geht.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <div className="flex items-start gap-3">
                      <MousePointer className="w-5 h-5 text-orange-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Große Klickbereiche</h3>
                        <p className="text-sm text-gray-700">Alle interaktiven Elemente sind mindestens 44x44 Pixel groß für einfachere Bedienung.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                    <div className="flex items-start gap-3">
                      <Volume2 className="w-5 h-5 text-pink-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Screenreader-Unterstützung</h3>
                        <p className="text-sm text-gray-700">Semantisches HTML und ARIA-Labels für optimale Screenreader-Kompatibilität.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-indigo-600 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Klare Struktur</h3>
                        <p className="text-sm text-gray-700">Logische Dokumentstruktur mit klaren Überschriften und konsistenter Navigation.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6" style={{borderColor: '#E1E4E8'}}>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Tastaturkürzel</h2>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between py-2 border-b" style={{borderColor: '#E1E4E8'}}>
                      <span className="text-gray-700">Navigation vorwärts</span>
                      <kbd className="px-2 py-1 bg-white border rounded text-xs font-mono" style={{borderColor: '#E1E4E8'}}>Tab</kbd>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b" style={{borderColor: '#E1E4E8'}}>
                      <span className="text-gray-700">Navigation rückwärts</span>
                      <kbd className="px-2 py-1 bg-white border rounded text-xs font-mono" style={{borderColor: '#E1E4E8'}}>Shift + Tab</kbd>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b" style={{borderColor: '#E1E4E8'}}>
                      <span className="text-gray-700">Element aktivieren</span>
                      <kbd className="px-2 py-1 bg-white border rounded text-xs font-mono" style={{borderColor: '#E1E4E8'}}>Enter / Space</kbd>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-gray-700">Menü schließen</span>
                      <kbd className="px-2 py-1 bg-white border rounded text-xs font-mono" style={{borderColor: '#E1E4E8'}}>Esc</kbd>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6" style={{borderColor: '#E1E4E8'}}>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Browser-Unterstützung</h2>
                <p className="text-gray-700 mb-4">Easyclaim24 ist optimiert für die neuesten Versionen folgender Browser:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl mb-2">🌐</div>
                    <p className="text-sm font-medium text-gray-900">Chrome</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl mb-2">🦊</div>
                    <p className="text-sm font-medium text-gray-900">Firefox</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl mb-2">🧭</div>
                    <p className="text-sm font-medium text-gray-900">Safari</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl mb-2">🌊</div>
                    <p className="text-sm font-medium text-gray-900">Edge</p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6" style={{borderColor: '#E1E4E8'}}>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Feedback & Verbesserungen</h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900 mb-3">Wir arbeiten kontinuierlich an der Verbesserung der Barrierefreiheit. Wenn Sie auf Barrieren stoßen oder Verbesserungsvorschläge haben, kontaktieren Sie uns bitte:</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-blue-800">📧 Email:</span>
                      <a href="mailto:barrierefreiheit@easyclaim24.de" className="text-blue-600 font-medium hover:underline">barrierefreiheit@easyclaim24.de</a>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-blue-800">📞 Telefon:</span>
                      <span className="text-blue-900 font-medium">+49 (0) 123 456789</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6" style={{borderColor: '#E1E4E8'}}>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Konformitätsstatus</h2>
                <p className="text-gray-700 mb-4">Wir streben die Konformität mit WCAG 2.1 Level AA an. Einige Bereiche der Software befinden sich noch in der Optimierung.</p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-900">⚠️ Diese Erklärung wurde zuletzt am <strong>Dezember 2024</strong> überprüft und aktualisiert.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Barrierefreiheit;
